/*
  BestBodyCode — Grundverhalten

  Es wird bewusst KEIN Scroll-Ereignis abgehört: das ist die
  klassische Ruckelquelle, weil der Browser dann bei jedem
  Scrollschritt neu rechnen muss.

  Kein Einblenden beim Hereinscrollen mehr: Inhalt, der auf einen
  Beobachter wartet, um sichtbar zu werden, bleibt irgendwann unsichtbar.
  Der Auftritt der Startseite läuft beim Laden und steht als Erstes in
  start() — er darf nicht ausfallen, weil weiter unten etwas wirft.
*/
(() => {
  const bewegungAus = () =>
    document.body.dataset.motion !== 'an';

  /* ---------------------------------------------------------------- *
     Auftritt der Startseite — läuft einmal beim Laden
   * ---------------------------------------------------------------- */
  function auftritt() {
    // Überschriften außerhalb der Startseiten-Bühne sofort aufdecken
    document.querySelectorAll('[data-vorhang]').forEach((el) => {
      if (!el.closest('[data-auftritt]')) requestAnimationFrame(() => el.classList.add('ist-sichtbar'));
    });
    const buehne = document.querySelector('[data-auftritt]');
    if (!buehne) return;

    if (bewegungAus()) {
      buehne.classList.add('ist-sichtbar');
      buehne.querySelectorAll('[data-vorhang]').forEach((el) => el.classList.add('ist-sichtbar'));
      return;
    }

    const los = () => requestAnimationFrame(() => {
      window.setTimeout(() => {
        buehne.classList.add('ist-sichtbar');
        buehne.querySelectorAll('[data-vorhang]').forEach((element, index) => {
          element.style.setProperty('--vorhang-delay', `${260 + index * 80}ms`);
          element.classList.add('ist-sichtbar');
        });
      }, 60);
    });

    // Läuft der Logo-Auftritt noch, verpufft der Hero hinter der Fläche —
    // also erst starten, wenn das Logo in den Kopf fliegt. Der Zeitgeber
    // sichert ab, dass der Hero nie unsichtbar bleibt.
    const intro = document.querySelector('.intro');
    if (intro && !intro.hasAttribute('data-flug') && intro.style.display !== 'none') {
      const notfall = window.setTimeout(los, 5000);
      document.addEventListener('bbc:intro-flug', () => {
        window.clearTimeout(notfall);
        los();
      }, { once: true });
      return;
    }
    los();
  }

  /* ---------------------------------------------------------------- *
     Kopfbereich verdichtet sich beim Scrollen
     Über einen unsichtbaren Fühler direkt unter dem Seitenanfang —
     dadurch ohne Scroll-Abhören.
   * ---------------------------------------------------------------- */
  function kopfbereich() {
    const kopf = document.querySelector('[data-kopf]');
    if (!kopf || !('IntersectionObserver' in window)) return;

    const fuehler = document.createElement('div');
    fuehler.setAttribute('aria-hidden', 'true');
    fuehler.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;';
    document.body.prepend(fuehler);

    new IntersectionObserver(
      ([eintrag]) => kopf.classList.toggle('ist-verdichtet', !eintrag.isIntersecting),
      { threshold: 0 }
    ).observe(fuehler);
  }

  /* ---------------------------------------------------------------- *
     Aufklapp-Bereiche mit weicher Höhe
     Nutzt grid-template-rows 0fr → 1fr, damit die Höhe nicht in
     JavaScript gemessen werden muss.
   * ---------------------------------------------------------------- */
  function aufklappen() {
    document.addEventListener('click', (ereignis) => {
      const kopf = ereignis.target.closest('[data-aufklapp-kopf]');
      if (!kopf) return;

      const eintrag = kopf.closest('[data-aufklapp]');
      if (!eintrag) return;

      const offen = eintrag.hasAttribute('open');
      const gruppe = eintrag.closest('[data-aufklapp-gruppe]');

      if (gruppe && !offen) {
        gruppe.querySelectorAll('[data-aufklapp][open]').forEach((anderer) => {
          if (anderer !== eintrag) {
            anderer.removeAttribute('open');
            anderer.querySelector('[data-aufklapp-kopf]')?.setAttribute('aria-expanded', 'false');
          }
        });
      }

      ereignis.preventDefault();
      if (offen) {
        eintrag.removeAttribute('open');
      } else {
        eintrag.setAttribute('open', '');
      }
      kopf.setAttribute('aria-expanded', offen ? 'false' : 'true');
    });
  }

  /* ---------------------------------------------------------------- *
     FAQ-Seite: nach Kategorie filtern und suchen
     Läuft ohne Neuladen. Ausgeblendet wird über [hidden]; die
     Einblendung ist gestaffelt, damit der Wechsel geführt wirkt.
   * ---------------------------------------------------------------- */
  function faqSeite() {
    document.querySelectorAll('[data-faq]').forEach((wurzel) => {
      if (wurzel.dataset.faqBereit === 'ja') return;
      wurzel.dataset.faqBereit = 'ja';

      const knoepfe = Array.from(wurzel.querySelectorAll('[data-faq-filter]'));
      const eintraege = Array.from(wurzel.querySelectorAll('[data-faq-eintrag]'));
      const suchfeld = wurzel.querySelector('[data-faq-suche]');
      const leerHinweis = wurzel.querySelector('[data-faq-leer]');
      const ruhig = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      let filter = 'alle';
      let suche = '';

      const anwenden = () => {
        let sichtbar = 0;

        eintraege.forEach((eintrag) => {
          const passtKategorie = filter === 'alle' || eintrag.dataset.kategorie === filter;
          const passtSuche = suche === '' || eintrag.textContent.toLowerCase().indexOf(suche) !== -1;

          if (passtKategorie && passtSuche) {
            eintrag.hidden = false;
            eintrag.style.setProperty('--eintritt-delay', ruhig ? '0ms' : sichtbar * 40 + 'ms');
            // Eintrittsanimation neu anstoßen
            eintrag.classList.remove('ist-eingetreten');
            void eintrag.offsetWidth;
            eintrag.classList.add('ist-eingetreten');
            sichtbar += 1;
          } else {
            eintrag.hidden = true;
            eintrag.removeAttribute('open');
            eintrag.querySelector('[data-aufklapp-kopf]')?.setAttribute('aria-expanded', 'false');
          }
        });

        if (leerHinweis) leerHinweis.hidden = sichtbar !== 0;
      };

      knoepfe.forEach((knopf) => {
        knopf.addEventListener('click', () => {
          filter = knopf.dataset.faqFilter;
          knoepfe.forEach((anderer) => {
            const aktiv = anderer === knopf;
            anderer.classList.toggle('ist-aktiv', aktiv);
            anderer.setAttribute('aria-pressed', aktiv ? 'true' : 'false');
          });
          anwenden();
        });
      });

      if (suchfeld) {
        let zeitgeber;
        suchfeld.addEventListener('input', () => {
          clearTimeout(zeitgeber);
          zeitgeber = setTimeout(() => {
            suche = suchfeld.value.trim().toLowerCase();
            anwenden();
          }, 140);
        });
      }

      anwenden();
    });
  }

  /* ---------------------------------------------------------------- *
     Widerrufsformular: Angaben prüfen, dann bestätigen

     Bewusst nur ein Schrittwechsel im DOM — abgesendet wird vom
     Formular selbst, nicht per fetch. Ohne dieses Skript sind beide
     Schritte sichtbar (siehe CSS), das Formular bleibt also benutzbar.
   * ---------------------------------------------------------------- */
  function widerruf() {
    document.querySelectorAll('[data-widerruf]').forEach((formular) => {
      if (formular.dataset.widerrufBereit === 'ja') return;
      formular.dataset.widerrufBereit = 'ja';

      const felder = formular.querySelector('[data-widerruf-felder]');
      const bestaetigen = formular.querySelector('[data-widerruf-bestaetigen]');
      const uebersicht = formular.querySelector('[data-widerruf-uebersicht]');
      const fehlend = formular.querySelector('[data-widerruf-fehlend]');
      const weiter = formular.querySelector('[data-widerruf-weiter]');
      const zurueck = formular.querySelector('[data-widerruf-zurueck]');

      if (!felder || !bestaetigen || !weiter) return;

      const eingaben = Array.from(formular.querySelectorAll('[data-widerruf-feld]'));

      const beschriftung = (feld) => {
        const marke = formular.querySelector(`label[for="${feld.id}"]`);
        return marke ? marke.textContent.trim() : feld.name;
      };

      // Datumsfelder liefern ISO ("2026-08-05") — in der Übersicht lesbar zeigen
      const anzeigewert = (feld) => {
        const wert = feld.value.trim();
        if (!wert || feld.type !== 'date') return wert;
        const teile = wert.split('-');
        return teile.length === 3 ? `${teile[2]}.${teile[1]}.${teile[0]}` : wert;
      };

      weiter.addEventListener('click', () => {
        // Erst die eingebaute Prüfung des Browsers, dann weiterschalten
        const luecke = eingaben.find((feld) => feld.required && !feld.value.trim());
        const zustimmung = formular.querySelector('.widerruf__zustimmung input[required]');

        if (luecke || (zustimmung && !zustimmung.checked)) {
          if (fehlend) fehlend.hidden = false;
          (luecke || zustimmung).focus({ preventScroll: false });
          return;
        }

        if (fehlend) fehlend.hidden = true;

        // Shopify liest den Absendernamen aus contact[name]
        const namensfeld = formular.querySelector('[data-widerruf-name]');
        if (namensfeld) {
          const vorname = formular.querySelector('[name="contact[Vorname]"]')?.value.trim() || '';
          const nachname = formular.querySelector('[name="contact[Nachname]"]')?.value.trim() || '';
          namensfeld.value = `${vorname} ${nachname}`.trim();
        }

        // Übersicht aus den tatsächlichen Werten aufbauen
        if (uebersicht) {
          uebersicht.textContent = '';
          eingaben.forEach((feld) => {
            const wert = anzeigewert(feld);
            if (!wert) return;

            const zeile = document.createElement('div');
            zeile.className = 'widerruf__zeile';

            const name = document.createElement('dt');
            name.textContent = beschriftung(feld);

            const inhalt = document.createElement('dd');
            // textContent, nicht innerHTML — Eingaben werden nie als Markup gedeutet
            inhalt.textContent = wert;

            zeile.append(name, inhalt);
            uebersicht.append(zeile);
          });
        }

        felder.classList.add('widerruf__felder-versteckt');
        bestaetigen.classList.add('ist-sichtbar');
        bestaetigen.querySelector('[data-widerruf-ueberschrift]')?.focus({ preventScroll: false });
      });

      zurueck?.addEventListener('click', () => {
        bestaetigen.classList.remove('ist-sichtbar');
        felder.classList.remove('widerruf__felder-versteckt');
        eingaben[0]?.focus({ preventScroll: false });
      });

      /*
        Die Angaben für die Eingangsbestätigung zwischenspeichern. Shopify
        leitet nach dem Absenden auf dieselbe Seite zurück und gibt die
        Werte dabei nicht mehr aus — ohne diesen Zwischenspeicher wüsste
        die Erfolgsseite die E-Mail-Adresse nicht mehr. sessionStorage
        gilt nur für diesen Tab und wird nach dem Melden sofort geleert.
      */
      formular.addEventListener('submit', () => {
        try {
          const daten = {};
          eingaben.forEach((feld) => {
            const wert = feld.value.trim();
            if (wert) daten[feld.name.replace(/^contact\[|\]$/g, '')] = wert;
          });
          daten.Zeitpunkt = new Date().toISOString();
          sessionStorage.setItem(WIDERRUF_SPEICHER, JSON.stringify(daten));
        } catch (fehler) {
          // Privater Modus ohne sessionStorage: dann bleibt der Weg von Hand
        }
      });
    });
  }

  const WIDERRUF_SPEICHER = 'bbc:widerruf';

  /* ---------------------------------------------------------------- *
     Eingangsbestätigung melden — § 356 Abs. 1 BGB

     Läuft nur auf der Erfolgsseite, also nachdem Shopify den Versand
     bestätigt hat. Schlägt die Meldung fehl, bleibt es dabei: es wird
     keine Erfolgsmeldung erzeugt und nichts beschönigt. Der Widerruf
     selbst liegt dann trotzdem schon im Postfach des Shops.
   * ---------------------------------------------------------------- */
  function widerrufMelden() {
    const marke = document.querySelector('[data-widerruf-melden]');
    if (!marke || marke.dataset.gemeldet === 'ja') return;

    const firma = marke.dataset.firma;
    if (!firma) return;

    let daten;
    try {
      const roh = sessionStorage.getItem(WIDERRUF_SPEICHER);
      if (!roh) return;
      daten = JSON.parse(roh);
      // Vor dem Senden leeren: ein Neuladen der Seite darf nicht doppelt melden
      sessionStorage.removeItem(WIDERRUF_SPEICHER);
    } catch (fehler) {
      return;
    }

    if (!daten || !daten.email) return;
    marke.dataset.gemeldet = 'ja';

    const { email, Vorname, Nachname, ...eigenschaften } = daten;

    fetch(`https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(firma)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            time: daten.Zeitpunkt,
            properties: eigenschaften,
            metric: {
              data: { type: 'metric', attributes: { name: marke.dataset.metrik || 'Widerruf eingegangen' } },
            },
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email, first_name: Vorname || '', last_name: Nachname || '' },
              },
            },
          },
        },
      }),
    }).catch(() => {
      /* Netzfehler: die Bestätigung muss dann von Hand raus */
    });
  }

  /* ---------------------------------------------------------------- *
     Mengenwähler
   * ---------------------------------------------------------------- */
  function mengen() {
    document.addEventListener('click', (ereignis) => {
      const knopf = ereignis.target.closest('[data-qty-schritt]');
      if (!knopf) return;

      const huelle = knopf.closest('[data-qty]');
      const feld = huelle?.querySelector('[data-qty-feld]');
      if (!feld) return;

      const schritt = parseInt(knopf.dataset.qtySchritt, 10);
      const min = parseInt(feld.min, 10) || 1;
      const wert = (parseInt(feld.value, 10) || min) + schritt;

      feld.value = Math.max(min, wert);
      feld.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ---------------------------------------------------------------- *
     Bildergalerie mit Überblendung
   * ---------------------------------------------------------------- */
  function galerie() {
    document.addEventListener('click', (ereignis) => {
      const daumen = ereignis.target.closest('[data-galerie-daumen]');
      if (!daumen) return;

      const galerieElement = daumen.closest('[data-galerie]');
      if (!galerieElement) return;

      const ziel = daumen.dataset.galerieDaumen;

      galerieElement.querySelectorAll('[data-galerie-bild]').forEach((bild) => {
        bild.classList.toggle('ist-aktiv', bild.dataset.galerieBild === ziel);
      });

      galerieElement.querySelectorAll('[data-galerie-daumen]').forEach((knopf) => {
        const aktiv = knopf === daumen;
        knopf.classList.toggle('ist-aktiv', aktiv);
        knopf.setAttribute('aria-selected', aktiv ? 'true' : 'false');
      });
    });
  }

  /* ---------------------------------------------------------------- *
     Mobiles Menü
   * ---------------------------------------------------------------- */
  function menue() {
    const menueElement = document.querySelector('[data-menue]');
    if (!menueElement) return;

    // Der Rest der Seite wird stillgelegt, solange das Menü offen ist —
    // sonst wandert Tab hinter die Blende. Das Menü selbst liegt im
    // Kopfbereich-Abschnitt, deshalb bleiben Lupe und Warenkorb erreichbar.
    const umgebung = () => document.querySelectorAll('main, .shopify-section-group-footer-group');

    const oeffnen = () => {
      menueElement.hidden = false;
      umgebung().forEach((el) => (el.inert = true));
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => menueElement.classList.add('ist-offen'));
      menueElement.querySelector('[data-menue-schliessen]')?.focus();
    };

    const schliessen = () => {
      menueElement.classList.remove('ist-offen');
      umgebung().forEach((el) => (el.inert = false));
      document.body.style.overflow = '';
      window.setTimeout(() => {
        menueElement.hidden = true;
      }, 320);
      document.querySelector('[data-menue-oeffnen]')?.focus();
    };

    document.addEventListener('click', (ereignis) => {
      if (ereignis.target.closest('[data-menue-oeffnen]')) {
        ereignis.preventDefault();
        oeffnen();
      } else if (ereignis.target.closest('[data-menue-schliessen]')) {
        ereignis.preventDefault();
        schliessen();
      }
    });

    document.addEventListener('keydown', (ereignis) => {
      if (ereignis.key === 'Escape' && !menueElement.hidden) schliessen();
    });
  }

  /* ---------------------------------------------------------------- *
     Auftauchen beim Hereinscrollen (Fallback fuer Browser ohne
     CSS-Scroll-Zeitleiste wie iOS Safari / WebKit)
   * ---------------------------------------------------------------- */
  function revealBeobachter() {
    if (bewegungAus()) return;
    if (window.CSS && CSS.supports && CSS.supports('animation-timeline: view()')) return;
    if (!('IntersectionObserver' in window)) return;

    const beobachter = new IntersectionObserver(
      (eintraege) => {
        eintraege.forEach((eintrag) => {
          if (eintrag.isIntersecting) {
            eintrag.target.classList.add('ist-sichtbar');
            beobachter.unobserve(eintrag.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -30px 0px' }
    );

    document.querySelectorAll('[data-reveal]:not([data-auftritt] [data-reveal])').forEach((el) => {
      beobachter.observe(el);
    });
  }

  /* ---------------------------------------------------------------- *
     Start
   * ---------------------------------------------------------------- */
  const start = () => {
    auftritt();
    revealBeobachter();
    kopfbereich();
    aufklappen();
    faqSeite();
    widerruf();
    widerrufMelden();
    mengen();
    galerie();
    menue();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Im Theme-Editor neu aufsetzen, wenn ein Abschnitt neu geladen wird
  document.addEventListener('shopify:section:load', () => {
    auftritt();
    revealBeobachter();
    faqSeite();
    widerruf();
  });
})();